import { ConfigProvider, Flex, Typography } from "antd";
import { ArrowUpOutlined, HistoryOutlined } from "@ant-design/icons";
import CardSectionTitle from "components/CardSectionTitle.component";
import { CustomerPackageCard } from "components/CustomerPackageCard.component";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import UpgradeCustomerPackageModal from "components/modals/UpgradeCustomerPackageModal.component";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { CustomerPackageDto } from "shared/types";
import { isCustomerPackageExpired } from "shared/utils";

export const UpgradeCustomerPackage: React.FC<{ customerPackage: CustomerPackageDto }> = ({ customerPackage }) => {
  const {
    new_customer_package: newCustomerPackage,
    previous_customer_package: previousCustomerPackage,
    customer_id: customerId,
    package_deal: { unlimited },
    expiry_date: expiryDate,
    is_valid: isValid,
  } = customerPackage;

  /**  Only can upgrade when:
    1. It never get upgraded
    2. Not an unlimited package
    3. Has not expired
    4. Is valid
  */
  const isExpired = isCustomerPackageExpired(expiryDate);
  // An upgrade is a new sale to the owner: never while they are deactivated
  // at the studio, even when a co-sharer keeps the package manageable.
  const canUpgrade =
    !newCustomerPackage &&
    !unlimited &&
    !isExpired &&
    isValid &&
    customerPackage.owner_active !== false;

  // Both preview cards open the package they describe. Without this the tab is
  // a dead end in both directions: you can see the package you upgraded from
  // (or to) but not reach its Invoice/Receipt, which live on its own page.
  // Deliberately a navigation rather than download buttons on the card itself:
  // the card is handed a NESTED copy of the package that omits upgrade_fee,
  // the discount fields and its own previous package, so a document built from
  // it would bill a chained upgrade at its stored purchase_price instead of
  // the fee actually paid. One page builds these documents, from the complete
  // payload, and that stays true.
  const navigate = useNavigate();
  const open = (id: string) => (e: React.MouseEvent) => {
    // Let the card's own controls (and any popover trigger) handle their click.
    if ((e.target as HTMLElement).closest("button")) return;
    navigate(`${PATHS.CUSTOMER_PACKAGE}/${id}`);
  };

  return (
    <Flex vertical gap={10}>
      <Flex align={"center"} justify="space-between" gap={16}>
        <CardSectionTitle title="Upgrade" subtitle={translate("subtitle.upgrade")} />
        {/* A membership is never upgradable, so it gets no button rather than
            a permanently disabled one. CustomerPackageDetails only mounts this
            tab on a membership that came out of an upgrade, to show the
            previous package below. Modal-buttons theme: navy upgrade CTA +
            ink Batal in the modal. */}
        {!unlimited && (
          <ConfigProvider theme={modalButtonsTheme}>
            <UpgradeCustomerPackageModal customerId={customerId} previousCustomerPackage={customerPackage} disabled={!canUpgrade} />
          </ConfigProvider>
        )}
      </Flex>

      {previousCustomerPackage && (
        <div>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            <HistoryOutlined style={{ marginRight: 8 }} />
            {translate("title.previous_customer_package")}
          </Typography.Title>
          <CustomerPackageCard
            customerPackage={previousCustomerPackage}
            isView
            hoverable
            onClick={open(previousCustomerPackage.id)}
          />
        </div>
      )}

      {/* The mirror of the block above. An upgraded-away package is reachable
          again since 2026-09-21 (it stays in the client's Transaksi tab as the
          real payment it was), so an admin can now land on one, and without
          this the tab was a dead end: a disabled Upgrade button and no trace of
          what the package had become. */}
      {newCustomerPackage && (
        <div>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            <ArrowUpOutlined style={{ marginRight: 8 }} />
            {translate("title.upgraded_to")}
          </Typography.Title>
          <CustomerPackageCard
            customerPackage={newCustomerPackage}
            isView
            hoverable
            onClick={open(newCustomerPackage.id)}
          />
        </div>
      )}
    </Flex>
  );
};
